import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";

/**
 * Subtle native-app-style page transitions. Skipped on desktop (≥768px) to
 * keep mouse-driven nav snappy, and respects prefers-reduced-motion.
 */
const PageTransition = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [enable, setEnable] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const update = () => setEnable(mq.matches && !reduce);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // IMPORTANT: keep the same React tree across breakpoints. Toggling between
  // `<>{children}</>` and `<AnimatePresence>` on viewport changes (e.g. when
  // the user rotates their phone) would remount children and reset all local
  // state (wizard steps, form inputs, etc.). Instead we always render the
  // motion wrapper and just disable the animation when not needed.
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={enable ? { opacity: 0, y: 8 } : false}
        animate={enable ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
        exit={enable ? { opacity: 0, y: -4 } : { opacity: 1 }}
        transition={enable ? { duration: 0.18, ease: [0.32, 0.72, 0, 1] } : { duration: 0 }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

export default PageTransition;
